import {
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { SelectOption } from '@fcom/common/interfaces';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';

import { PopoverOptions } from '../../popover';

@Component({
  selector: 'fin-select-input',
  templateUrl: './select-input.component.html',
  styleUrls: ['./select-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SelectInputComponent {
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly FinnairTheme = FinnairTheme;

  themeService = inject(FinnairThemeService);
  parentForm = input.required<UntypedFormGroup>();
  controlName = input.required<string>();
  translationPrefix = input<string>();
  options = input.required<SelectOption[]>();
  id = input<string>();
  selectLabelTranslationKey = input('select');
  required = input<boolean>();
  displayMargin = input(true);
  label = input<string>();
  tooltipHeading = input<string>();
  tooltipContent = input<string>();
  tooltipOptions = input<PopoverOptions>();
  disableLabel = input(false);
  disableSelectOption = input(false);

  readonly ctrlField = computed(() => this.parentForm().get(this.controlName()));
  readonly control = computed(() => this.ctrlField());
  readonly idOrControlName = computed<string>(() => this.id() || this.controlName());

  readonly selectElement = viewChild.required<ElementRef>('selectElement');

  get fieldRequired(): boolean {
    return Boolean(this.required() || this.ctrlField()?.hasValidator(Validators.required));
  }

  get showAsterisk(): boolean {
    return this.required() ?? this.fieldRequired;
  }

  constructor() {
    effect(() => {
      const options = this.options();
      const ctrlField = this.ctrlField();

      if (!ctrlField || !options) {
        return;
      }

      const hasValueInOptions = options.find((option) => option.value === ctrlField.value);

      if (ctrlField.value && !hasValueInOptions) {
        ctrlField.patchValue('');
      }
    });
  }

  setFocus(): void {
    this.selectElement().nativeElement.focus();
  }
}
